<template>
  <div class="up-next-panel">
    <div ref="scroller" class="up-next-panel__body">
      <div class="up-next-panel__head">
        <span class="up-next-panel__title">Run List</span>
        <span class="up-next-panel__count">{{ items.length }} items</span>
      </div>

      <UpNextRow
        v-for="(item, index) in items"
        :key="item.id ?? item.run"
        :run="item.run"
        :year="item.year"
        :title="item.title"
        :rep-required="item.repRequired"
        :high-bid="item.highBid"
        :badge="item.badge"
        :status="item.status"
        :on-block="item.onBlock"
        :highlighted="item.highlighted"
        :last="index === items.length - 1"
        :data-on-block="item.onBlock ? 'true' : null"
        @move="emit('move', item)"
      />

      <div v-if="items.length === 0" class="up-next-panel__empty">No items in the run list.</div>
    </div>

    <CrossEventDrawer
      :found-run="foundRun"
      :moved="moved"
      :target-event-name="targetEventName"
      :target-lane="targetLane"
      @move="emit('cross-event-move')"
      @close="emit('cross-event-close')"
    />
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';
import UpNextRow from '../UpNextRow/UpNextRow.vue';
import CrossEventDrawer from '../CrossEventDrawer/CrossEventDrawer.vue';

// The run list — every lot in the sale, past, present and upcoming, with the
// cross-event sheet layered over it.
//
// It used to resolve the run list itself out of two stores, hold a mutable copy
// of it, listen on `window` for a `clerk:findRun` event the search field
// dispatched, and call `moveToItem` on a double-click. All four are gone: it
// takes the rows, and reports which one the clerk asked for.
//
// The one thing it still does on its own is SCROLL, because that is about this
// element's viewport and nothing else could do it. When the block advances the
// on-block row is centered where there is room and clamped to the scroll range
// where there is not — without the clamp the last few lots over-scroll into
// blank space below the list.
defineOptions({ name: 'UpNextPanel' });

const props = defineProps({
  // [{ id, run, year, title, repRequired, highBid, badge, status, onBlock,
  //    highlighted }] in run order.
  items: { type: Array, default: () => [] },
  // A lot found in another event, awaiting a decision. See CrossEventDrawer.
  foundRun: { type: Object, default: null },
  moved: { type: Boolean, default: false },
  targetEventName: { type: String, default: '' },
  targetLane: { type: String, default: '' },
});

const emit = defineEmits(['move', 'cross-event-move', 'cross-event-close']);

const scroller = ref(null);

function scrollToOnBlock() {
  const container = scroller.value;
  const row = container?.querySelector('[data-on-block="true"]');
  if (!container || !row) return;

  const containerBox = container.getBoundingClientRect();
  const rowBox = row.getBoundingClientRect();
  const centered = container.scrollTop + (rowBox.top - containerBox.top) - (container.clientHeight - rowBox.height) / 2;
  const furthest = container.scrollHeight - container.clientHeight;

  container.scrollTo({ top: Math.max(0, Math.min(centered, furthest)), behavior: 'smooth' });
}

// Keyed on WHICH row is on the block rather than on the list, so re-rendering
// the same list with fresh bids does not yank the clerk's scroll position.
watch(
  () => props.items.find((item) => item.onBlock)?.id ?? props.items.find((item) => item.onBlock)?.run,
  () => nextTick(scrollToOnBlock)
);

onMounted(() => nextTick(scrollToOnBlock));
</script>

<style scoped>
.up-next-panel {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: var(--p-up-next-panel-background);
  border: var(--p-up-next-panel-border-width) solid var(--p-up-next-panel-border-color);
  border-radius: var(--p-up-next-panel-border-radius);
}

.up-next-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.up-next-panel__head {
  flex: 0 0 auto;
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--p-up-next-panel-head-padding);
  border-bottom: var(--p-up-next-panel-head-border-bottom-width) solid
    var(--p-up-next-panel-head-border-bottom-color);
  background: var(--p-up-next-panel-head-background);
}

.up-next-panel__title {
  text-transform: uppercase;
  font-size: var(--p-up-next-panel-title-font-size);
  font-weight: var(--p-up-next-panel-title-font-weight);
  letter-spacing: var(--p-up-next-panel-title-letter-spacing);
  color: var(--p-up-next-panel-title-color);
}

.up-next-panel__count {
  font-family: var(--p-up-next-panel-count-font-family);
  font-size: var(--p-up-next-panel-count-font-size);
  color: var(--p-up-next-panel-count-color);
}

.up-next-panel__empty {
  font-style: italic;
  padding: var(--p-up-next-panel-empty-padding);
  font-size: var(--p-up-next-panel-empty-font-size);
  color: var(--p-up-next-panel-empty-color);
}
</style>
